import { Component, OnInit } from '@angular/core';
import { AdminService } from '../../services/admin/admin.service';
import { ActivatedRoute } from '@angular/router';
import { HelpersService } from '../../../shared/servicio/helpers/helpers.service';

@Component({
  selector: 'app-ver-encuestas',
  templateUrl: './ver-encuestas.component.html',
  styleUrls: ['./ver-encuestas.component.css']
})
export class VerEncuestasComponent implements OnInit {
  // Datos de la encuesta
  encuestaInfo: any;
  surveyStatistics: any;
  idEncuesta: number;

  // Configuración de API
  useNewApi: boolean = true;

  /**
   * Arreglo que contiene un ARREGLO de puntos por cada opción de respuesta de cada pregunta
   */
  answerCountPerOption: number[][] = [];

  /**
   * Arreglo que contiene los titulos de las preguntas
   */
  arrQuestions: any[] = [];

  /**
   * Arreglo que contiene las opciones de respuesta por pregunta
   */
  answerSelectionList: string[][] = [];

  /**
   * Variable booleana que indica si se muestra el loading o no
   */
  showLoading: boolean = true;

  /**
   * Datos para análisis de texto (preguntas abiertas)
   */
  textAnalysisData: any = {};

  /**
   * Datos de respuestas por especialidad
   */
  responsesBySpecialty: any = {};

  /**
   * Estadísticas generales
   */
  generalStats: any = {};

  constructor(
    private admin: AdminService,
    private activeRoute: ActivatedRoute,
    private helper: HelpersService
  ) { }

  ngOnInit() {
    this.initializeComponent();
    this.loadRouteParams();
  }

  /**
   * Inicializa el componente
   */
  private initializeComponent() {
    this.showLoading = true;
    this.answerCountPerOption = [];
    this.arrQuestions = [];
    this.answerSelectionList = [];
    this.textAnalysisData = {};
    this.responsesBySpecialty = {};
    this.generalStats = {};
  }

  /**
   * Carga los parámetros de la ruta
   */
  private loadRouteParams() {
    this.activeRoute.params.subscribe(params => {
      this.idEncuesta = parseInt(params.id);
      if (this.idEncuesta) {
        this.loadEncuesta();
      }
    });
  }

  /**
   * Carga los datos de la encuesta
   */
  loadEncuesta() {
    this.showLoading = true;

    // Cargar detalles básicos de la encuesta
    this.loadSurveyDetails();

    // Cargar estadísticas si usa nueva API
    if (this.useNewApi) {
      this.loadSurveyStatistics();
    }
  }

  /**
   * Carga los detalles de la encuesta
   */
  private loadSurveyDetails() {
    const detailsMethod = this.useNewApi ?
      this.admin.getSurveyDetailsNew(this.idEncuesta) :
      this.admin.list_idSurvey(this.idEncuesta);

    detailsMethod.subscribe({
      next: (data) => {
        this.encuestaInfo = data;
        this.processEncuestaData(data);
      },
      error: (error) => {
        console.error('Error loading survey details:', error);
        this.showLoading = false;
        this.helper.modalAlert('Error al cargar los detalles de la encuesta');
      }
    });
  }

  /**
   * Carga las estadísticas de la encuesta (solo nueva API)
   */
  private loadSurveyStatistics() {
    this.admin.getSurveyStatistics(this.idEncuesta).subscribe({
      next: (stats) => {
        this.surveyStatistics = stats;
        this.processStatisticsData(stats);
      },
      error: (error) => {
        console.error('Error loading survey statistics:', error);
        // No mostrar error ya que las estadísticas son opcionales
      }
    });
  }

  /**
   * Procesa los datos de la encuesta
   */
  private processEncuestaData(data: any) {
    const questions = data.body || [];

    this.loadQuestionsInfo(questions);
    this.getQuestions(questions);
    this.getOptionsOfQuestion(questions);
    this.processOpenQuestions(questions);

    this.showLoading = false;
  }

  /**
   * Procesa los datos de estadísticas
   */
  private processStatisticsData(stats: any) {
    this.generalStats = {
      totalQuestions: stats.survey_info.total_questions || 0,
      totalResponses: this.calculateTotalResponses(stats.questions_stats),
      averageResponseTime: this.calculateAverageResponseTime(stats.questions_stats),
      completionRate: this.calculateCompletionRate(stats.questions_stats)
    };

    // Procesar respuestas por especialidad si están disponibles
    this.processResponsesBySpecialty(stats.questions_stats);
  }

  /**
   * Procesa preguntas abiertas para análisis de texto
   */
  private processOpenQuestions(questions: any[]) {
    questions.forEach((question, index) => {
      if (question.question.question_type === 'open' && question.open_responses) {
        this.textAnalysisData[index] = {
          question: question.question.question,
          responses: question.open_responses,
          totalResponses: question.open_responses.length,
          responsesBySpecialty: this.groupResponsesBySpecialty(question.open_responses)
        };
      }
    });
  }

  /**
   * Agrupa respuestas por especialidad
   */
  private groupResponsesBySpecialty(responses: any[]): any {
    const grouped = {};

    responses.forEach(response => {
      const specialty = response.specialty || 'General';
      if (!grouped[specialty]) {
        grouped[specialty] = [];
      }
      grouped[specialty].push(response);
    });

    return grouped;
  }

  /**
   * Procesa respuestas por especialidad
   */
  private processResponsesBySpecialty(questionsStats: any[]) {
    questionsStats.forEach(question => {
      if (question.responses_by_specialty) {
        this.responsesBySpecialty[question.question_id] = question.responses_by_specialty;
      }
    });
  }

  /**
   * Calcula el total de respuestas
   */
  private calculateTotalResponses(questionsStats: any[]): number {
    if (!questionsStats || questionsStats.length === 0) return 0;

    const values = questionsStats.map(q => q.total_votes || q.total_responses || 0);
    return values.reduce((max, val) => val > max ? val : max, 0);
  }


  /**
   * Calcula el tiempo promedio de respuesta
   */
  private calculateAverageResponseTime(questionsStats: any[]): number {
    // Implementar lógica para calcular tiempo promedio si está disponible
    return 0; // Por ahora retorna 0
  }

  /**
   * Calcula la tasa de finalización
   */
  private calculateCompletionRate(questionsStats: any[]): number {
    if (!questionsStats || questionsStats.length === 0) return 0;

    const totalQuestions = questionsStats.length;
    const completedQuestions = questionsStats.filter(q =>
      (q.total_votes || q.total_responses || 0) > 0
    ).length;

    return totalQuestions > 0 ? (completedQuestions / totalQuestions) * 100 : 0;
  }

  /**
   * Función para obtener las opciones de cada pregunta en un array
   */
  getOptionsOfQuestion(body: any[]) {
    this.answerSelectionList = [];

    body.forEach(question => {
      if (question.question.question_type === 'closed' || question.question.question_type === 'categorized') {
        const optionsList = this.getOptionsCount(question.answers);
        this.answerSelectionList.push(optionsList);
      } else {
        // Para preguntas abiertas, no hay opciones predefinidas
        this.answerSelectionList.push([]);
      }
    });
  }

  /**
   * Función que toma una lista de opciones de respuesta de una pregunta y la mete en un arreglo
   */
  getOptionsCount(options: any[]): string[] {
    if (!options) return [];

    return options.map(option => option.answer || '');
  }

  /**
   * Función que toma la lista de preguntas de una encuesta para calcular la
   * cantidad de votos que obtuvo en cada opción de respuesta
   */
  loadQuestionsInfo(questions: any[]) {
    this.answerCountPerOption = [];

    questions.forEach(question => {
      if (question.question.question_type === 'closed' || question.question.question_type === 'categorized') {
        const answerList = this.getAnswersCount(question.answers);
        this.answerCountPerOption.push(answerList);
      } else {
        // Para preguntas abiertas, no hay votos por opción
        this.answerCountPerOption.push([]);
      }
    });
  }

  /**
   * Función que toma una lista de opciones de respuesta y cuenta sus votos
   */
  getAnswersCount(options: any[]): number[] {
    if (!options) return [];

    return options.map(option => option.votes || 0);
  }

  /**
   * Función para obtener los nombres de las preguntas en un array
   */
  getQuestions(questions: any[]) {
    this.arrQuestions = [];

    questions.forEach(question => {
      this.arrQuestions.push({
        question: question.question.question,
        type: question.question.question_type,
        specialtyFilter: question.question.specialty_filter
      });
    });
  }

  /**
   * Exporta la encuesta a Excel
   */
  exportToExcel() {
    if (!this.useNewApi) {
      this.helper.modalAlert('La exportación a Excel requiere la nueva API');
      return;
    }

    this.admin.exportSurveyToExcel(this.idEncuesta).subscribe({
      next: (blob) => {
        this.downloadFile(blob, `encuesta_${this.idEncuesta}.xlsx`);
        this.helper.modalSuccess('¡Encuesta exportada exitosamente!');
      },
      error: (error) => {
        console.error('Error exporting survey:', error);
        this.helper.modalAlert('Error al exportar la encuesta');
      }
    });
  }

  /**
   * Descarga un archivo blob
   */
  private downloadFile(blob: Blob, filename: string) {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  /**
   * Obtiene análisis detallado de texto para una pregunta
   */
  getDetailedTextAnalysis(questionId: number) {
    if (!this.useNewApi) {
      this.helper.modalAlert('El análisis de texto requiere la nueva API');
      return;
    }

    this.admin.getDetailedTextAnalysis(questionId).subscribe({
      next: (analysis) => {
        console.log('Text analysis:', analysis);
        this.helper.modalSuccess('Análisis de texto obtenido exitosamente');
        // Aquí puedes implementar la lógica para mostrar el análisis
      },
      error: (error) => {
        console.error('Error getting text analysis:', error);
        this.helper.modalAlert('Error al obtener el análisis de texto');
      }
    });
  }

  /**
   * Alterna entre nueva API y API legacy
   */
  toggleApiMode() {
    this.useNewApi = !this.useNewApi;
    this.loadEncuesta();
    this.helper.modalSuccess(`Cambiado a ${this.useNewApi ? 'nueva' : 'antigua'} API`);
  }

  /**
   * Obtiene el tipo de pregunta como texto
   */
  getQuestionTypeText(type: string): string {
    const types = {
      'closed': 'Cerrada',
      'open': 'Abierta',
      'categorized': 'Categorizada'
    };
    return types[type] || type;
  }

  /**
   * Verifica si una pregunta tiene filtro de especialidad
   */
  hasSpecialtyFilter(question: any): boolean {
    return question.specialtyFilter && question.specialtyFilter.length > 0;
  }

  /**
   * Obtiene las especialidades filtradas de una pregunta
   */
  getSpecialtyFilters(question: any): string[] {
    if (!question.specialtyFilter) return [];

    try {
      return typeof question.specialtyFilter === 'string'
        ? JSON.parse(question.specialtyFilter)
        : question.specialtyFilter;
    } catch (e) {
      return [];
    }
  }

  /**
   * Verifica si hay datos de análisis de texto para mostrar
   */
  hasTextAnalysis(): boolean {
    return Object.keys(this.textAnalysisData).length > 0;
  }

  /**
   * Obtiene el porcentaje de una respuesta
   */
  getAnswerPercentage(votes: number, questionIndex: number): number {
    const totalVotes = this.answerCountPerOption[questionIndex]
      .reduce((sum, count) => sum + count, 0) || 0;

    return totalVotes > 0 ? Math.round((votes / totalVotes) * 100) : 0;
  }

  /**
   * Obtiene el color para una barra de progreso basado en el porcentaje
   */
  getProgressBarColor(percentage: number): string {
    if (percentage >= 70) return 'success';
    if (percentage >= 40) return 'warning';
    return 'danger';
  }

  /**
   * Obtiene el total de votos para una pregunta específica
   */
  getTotalVotesForQuestion(questionIndex: number): number {
    if (!this.answerCountPerOption[questionIndex]) return 0;
    return this.answerCountPerOption[questionIndex].reduce((sum, count) => sum + count, 0);
  }

  /**
   * Obtiene la letra correspondiente a una respuesta
   */
  getAnswerLetter(answerIndex: number): string {
    return String.fromCharCode(65 + answerIndex);
  }

  /**
   * Obtiene el número de votos para una respuesta específica
   */
  getAnswerVotes(questionIndex: number, answerIndex: number): number {
    if (!this.answerCountPerOption[questionIndex] || !this.answerCountPerOption[questionIndex][answerIndex]) {
      return 0;
    }
    return this.answerCountPerOption[questionIndex][answerIndex];
  }

  /**
   * Obtiene la clase CSS para la barra de progreso
   */
  getProgressBarClass(questionIndex: number, answerIndex: number): string {
    const votes = this.getAnswerVotes(questionIndex, answerIndex);
    const percentage = this.getAnswerPercentage(votes, questionIndex);
    return 'bg-' + this.getProgressBarColor(percentage);
  }

  /**
   * Verifica si hay estadísticas generales disponibles
   */
  hasGeneralStats(): boolean {
    return Object.keys(this.generalStats).length > 0;
  }

  /**
   * Verifica si hay respuestas por especialidad
   */
  hasResponsesBySpecialty(): boolean {
    return Object.keys(this.responsesBySpecialty).length > 0;
  }

  /**
   * Formatea un número como porcentaje
   */
  formatPercentage(value: number): string {
    return `${Math.round(value)}%`;
  }

  /**
   * Formatea una fecha
   */
  formatDate(dateString: string): string {
    if (!dateString) return '';

    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  /**
   * Refresca los datos de la encuesta
   */
  refreshData() {
    this.loadEncuesta();
  }

  /**
   * Limpieza al destruir el componente
   */
  ngOnDestroy() {
    // Limpiar suscripciones si es necesario
  }
}
